import { MoreHoriz } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import ConfirmationModal from '../../ConfirmationModal';

type MoreOptionsProps = {
  onDelete?: () => void;
  onArchive?: () => void;
  onDuplicate?: () => void;
  name?: string;
  itemName: string;
};

const MoreOptions: React.FC<MoreOptionsProps> = ({ onDelete, onArchive, name, itemName, onDuplicate }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const { disabled, reasons } = useContext(DisableContext);
  const disabledOtherThanArchived = disabled && !reasons.archived;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setIsDeleteConfirmOpen(true);
  };

  const handleArchiveClick = () => {
    setAnchorEl(null);
    setIsArchiveConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteConfirmOpen(false);
    onDelete?.();
  };

  const handleConfirmArchive = () => {
    setIsArchiveConfirmOpen(false);
    onArchive?.();
  };

  const handleDuplicateClick = () => {
    setAnchorEl(null);
    onDuplicate?.();
  };

  return (
    <Box display="flex" alignItems="center">
      <ResponsiveButton variant="outlined" onClick={handleOpen} startIcon={<MoreHoriz />} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <MoreHoriz />
      </ResponsiveButton>
      <IconButton onClick={handleOpen} sx={{ p: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {onArchive && (
          <MenuItem onClick={handleArchiveClick} disabled={disabled} sx={{ color: 'orange' }}>
            Archive
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDeleteClick} disabled={disabledOtherThanArchived} sx={{ color: 'red' }}>
            Delete
          </MenuItem>
        )}
        {onDuplicate && (
          <MenuItem onClick={handleDuplicateClick} disabled={disabledOtherThanArchived}>
            Duplicate
          </MenuItem>
        )}
      </Menu>

      {onArchive && (
        <ConfirmationModal
          text={`Are you sure you want to archive the ${itemName} ${name}? Archiving will not destroy the data but it will prevent it from being active in Schedulink in any way. The operation can only be reversed by contacting support.`}
          isOpen={isArchiveConfirmOpen}
          onCancel={() => setIsArchiveConfirmOpen(false)}
          onConfirm={handleConfirmArchive}
        />
      )}

      <ConfirmationModal
        text={`Are you sure you want to delete the ${itemName} ${name}? This operation is irreversible!`}
        isOpen={isDeleteConfirmOpen}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default MoreOptions;
